/**
 * Patient appointment timeline (history view)
 * Refined design with golden accents - entire card is clickable
 */

import { useNavigate } from 'react-router-dom'
import { Calendar as CalendarIcon } from 'lucide-react'
import { formatDateCzech } from '@/lib/calendar-utils'
import { STATUS_BACKGROUNDS, isCancelledBackground } from '@/lib/constants'
import type { PatientAppointment } from '@/types/patient'

interface PatientTimelineProps {
	appointments: PatientAppointment[]
}

export function PatientTimeline({ appointments }: PatientTimelineProps) {
	const navigate = useNavigate()

	// Group appointments by year and month
	const groupedAppointments = groupByMonth(appointments)

	const handleGoToAppointment = (apt: PatientAppointment) => {
		const type = apt.type.toLowerCase()
		navigate(`/calendar/${type}?date=${apt.date}`)
	}

	if (appointments.length === 0) {
		return (
			<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
				<h2 className="mb-4 text-lg font-semibold text-[#3d3428]">Historie termínů</h2>
				<div className="flex flex-col items-center justify-center py-12 text-[#8c8275]">
					<div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#f5ebe0] mb-4">
						<CalendarIcon className="h-8 w-8 text-[#d7ad6c] opacity-60" />
					</div>
					<p className="text-[#a09689]">Žádné termíny v historii</p>
				</div>
			</div>
		)
	}

	return (
		<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
			<h2 className="mb-4 text-lg font-semibold text-[#3d3428]">Historie termínů</h2>

			<div className="space-y-6">
				{Object.entries(groupedAppointments).map(([monthKey, monthAppts]) => (
					<div key={monthKey}>
						<h3 className="mb-3 text-sm font-medium text-[#a09689] capitalize">
							{formatMonthHeader(monthKey)}
						</h3>
						<div className="space-y-2">
							{monthAppts.map((apt) => (
								<AppointmentRow
									key={apt.id}
									appointment={apt}
									onGoTo={() => handleGoToAppointment(apt)}
								/>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

interface AppointmentRowProps {
	appointment: PatientAppointment
	onGoTo: () => void
}

function AppointmentRow({ appointment, onGoTo }: AppointmentRowProps) {
	const bg = appointment.background
	const isNoShow = bg === STATUS_BACKGROUNDS.NO_SHOW || bg === '#d50000'
	const isCancelled =
		bg === STATUS_BACKGROUNDS.CANCELLED || bg === '#616161' || isCancelledBackground(bg)
	const isPast = appointment.date < new Date().toISOString().split('T')[0]

	// Type badge colors matching search results
	const typeBadgeStyles = {
		ZUB: 'bg-[#f5ebe0] text-[#a88544]',
		DH: 'bg-blue-100 text-blue-700',
	}
	const badgeStyle = typeBadgeStyles[appointment.type as keyof typeof typeBadgeStyles] || 'bg-gray-100 text-gray-600'

	return (
		<button
			type="button"
			onClick={onGoTo}
			className={`w-full flex items-center gap-4 rounded-xl border border-[#f0ede8] p-3 transition-colors hover:bg-[#faf8f5] hover:border-[#e5c896] cursor-pointer text-left ${
				isCancelled ? 'opacity-60' : ''
			}`}
		>
			{/* Color indicator */}
			<div
				className="h-10 w-1.5 flex-shrink-0 rounded-full"
				style={{ backgroundColor: bg || '#d7ad6c' }}
			/>

			{/* Date */}
			<div className="w-20 flex-shrink-0 text-center">
				<p className="text-lg font-semibold text-[#3d3428]">
					{formatDateCzech(new Date(appointment.date), 'd.')}
				</p>
				<p className="text-xs text-[#a09689] capitalize">
					{formatDateCzech(new Date(appointment.date), 'EEEE')}
				</p>
			</div>

			{/* Time */}
			<div className="w-24 flex-shrink-0 text-sm text-[#5c5142] font-medium">
				{appointment.startTime} - {appointment.endTime}
			</div>

			{/* Title */}
			<div className="min-w-0 flex-1">
				<p
					className={`truncate font-medium ${
						isCancelled ? 'text-[#a09689]' : 'text-[#3d3428]'
					}`}
				>
					{appointment.title || 'Bez názvu'}
				</p>
				{appointment.note && (
					<p className="truncate text-sm text-[#8c8275]">{appointment.note}</p>
				)}
			</div>

			{/* Status badge */}
			<div className="flex-shrink-0">
				{isNoShow && (
					<span className="rounded-lg bg-red-50 border border-red-100 px-2.5 py-1 text-xs font-medium text-red-600">
						Nepřišel
					</span>
				)}
				{isCancelled && !isNoShow && (
					<span className="rounded-lg bg-gray-50 border border-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
						Zrušeno
					</span>
				)}
				{!isPast && !isCancelled && !isNoShow && (
					<span className="rounded-lg bg-blue-50 border border-blue-100 px-2.5 py-1 text-xs font-medium text-blue-600">
						Nadcházející
					</span>
				)}
			</div>

			{/* Type badge */}
			<span className={`flex-shrink-0 rounded-lg px-2.5 py-1 text-xs font-medium ${badgeStyle}`}>
				{appointment.type}
			</span>
		</button>
	)
}

/**
 * Group appointments by year-month
 */
function groupByMonth(
	appointments: PatientAppointment[],
): Record<string, PatientAppointment[]> {
	const sorted = [...appointments].sort(
		(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
	)

	return sorted.reduce(
		(groups, apt) => {
			const key = apt.date.substring(0, 7) // YYYY-MM
			if (!groups[key]) {
				groups[key] = []
			}
			groups[key].push(apt)
			return groups
		},
		{} as Record<string, PatientAppointment[]>,
	)
}

/**
 * Format month key to Czech month header
 */
function formatMonthHeader(monthKey: string): string {
	const [year, month] = monthKey.split('-')
	const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)
	return formatDateCzech(date, 'LLLL yyyy')
}
